"use client"

/**
 * The profile-menu door to sign-in flows: pick one here, author one next door.
 */

import * as React from "react"
import { Link } from "react-router"
import { useProductAuthoringEnabled } from "@exxatdesignux/ui/components/shell"

import { ProfileMenuSubTrigger } from "@/components/profile-menu-sub-trigger"
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu"
import { signOut } from "@/lib/auth-session"
import {
  getActiveFlowId,
  getLoginFlows,
  setActiveFlowId,
  type LoginFlowDefinition,
} from "@/lib/login-flow"
import { signInRolePath } from "@/lib/sign-in-role-link"
import { SIGN_IN_FLOWS_PATH } from "@/lib/sign-in-flows-shell"
import {
  SIGN_IN_FLOW_GROUPS,
  signInFlowShapes,
  signInFlowGroupMembers,
  signInFlowUncategorizedRoles,
  signInFlowPersonaGroupForFlow,
} from "@/lib/sign-in-flow-groups"

export { SIGN_IN_FLOWS_PATH }

function activeFlowLabel(
  flows: readonly LoginFlowDefinition[],
  activeId: string,
): string | undefined {
  const active = flows.find(flow => flow.id === activeId)
  if (!active) return undefined
  const groupId = signInFlowPersonaGroupForFlow(activeId)
  if (!groupId) return active.name
  const group = SIGN_IN_FLOW_GROUPS.find(entry => entry.id === groupId)
  return group ? `${group.label}: ${active.name}` : active.name
}

function FlowLinkList({
  flows,
  activeId,
  onSelect,
}: {
  flows: readonly LoginFlowDefinition[]
  activeId: string
  onSelect: (id: string) => void
}) {
  return flows.map(flow => (
    <DropdownMenuItem key={flow.id} asChild>
      <Link to={signInRolePath(flow.id)} className="cursor-pointer" onClick={() => onSelect(flow.id)}>
        {flow.name}
        {flow.id === activeId ? (
          <i className="fa-light fa-check ml-auto" aria-hidden="true" />
        ) : null}
      </Link>
    </DropdownMenuItem>
  ))
}

export function SignInFlowMenuItem() {
  const authoringEnabled = useProductAuthoringEnabled()

  const [open, setOpen] = React.useState(false)
  const [flows, setFlows] = React.useState<LoginFlowDefinition[]>([])
  const [activeId, setActiveId] = React.useState(() =>
    authoringEnabled ? getActiveFlowId() : "",
  )

  React.useEffect(() => {
    if (!authoringEnabled) return
    setFlows(getLoginFlows())
    setActiveId(getActiveFlowId())
  }, [authoringEnabled])

  React.useEffect(() => {
    if (!authoringEnabled || !open) return
    setFlows(getLoginFlows())
    setActiveId(getActiveFlowId())
  }, [authoringEnabled, open])

  if (!authoringEnabled) return null

  const shapes = signInFlowShapes(flows)
  const uncategorized = signInFlowUncategorizedRoles(flows)

  function run(id: string) {
    setActiveFlowId(id)
    setActiveId(id)
    signOut()
  }

  return (
    <DropdownMenuSub open={open} onOpenChange={setOpen}>
      <ProfileMenuSubTrigger
        iconClassName="fa-light fa-route"
        label="Sign-in flow"
        trailing={activeFlowLabel(flows, activeId)}
      />
      <DropdownMenuSubContent className="max-h-[min(32rem,var(--radix-dropdown-menu-content-available-height))] w-60 overflow-y-auto">
        <DropdownMenuLabel>Signs out and runs it</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <FlowLinkList flows={shapes} activeId={activeId} onSelect={run} />

        {SIGN_IN_FLOW_GROUPS.map(group => {
          const members = signInFlowGroupMembers(flows, group.id)
          if (members.length === 0) return null
          return (
            <DropdownMenuSub key={group.id}>
              <ProfileMenuSubTrigger
                iconClassName={group.icon}
                label={group.label}
                trailing={members.length}
              />
              <DropdownMenuSubContent className="max-h-[min(28rem,var(--radix-dropdown-menu-content-available-height))] w-72 overflow-y-auto">
                <FlowLinkList flows={members} activeId={activeId} onSelect={run} />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )
        })}

        {uncategorized.length > 0 ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Other roles</DropdownMenuLabel>
            <FlowLinkList flows={uncategorized} activeId={activeId} onSelect={run} />
          </>
        ) : null}

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Link to={SIGN_IN_FLOWS_PATH} className="cursor-pointer">
            <i className="fa-light fa-sliders" aria-hidden="true" />
            Configure
          </Link>
        </DropdownMenuItem>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}
